import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy, ComponentType } from "react";
import { AuthForm } from "./components/auth/AuthForm";
import { useActivityTracking } from "./hooks/useActivityTracking";
import { useUserRole } from "./hooks/useUserRole";
import { usePhotographyAccess } from "./hooks/usePhotographyAccess";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Helper to handle chunk loading errors with retry and page reload
function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      // If chunk loading fails, reload the page to get fresh chunks
      console.error('Chunk loading failed, reloading page...', error);
      window.location.reload();
      // Return a placeholder to satisfy TypeScript (will never render due to reload)
      return { default: (() => null) as unknown as T };
    }
  });
}

// Lazy load pages for code splitting with retry on failure
const SignUp = lazyWithRetry(() => import("./pages/SignUp"));
const VerifyEmail = lazyWithRetry(() => import("./pages/VerifyEmail"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Leads = lazyWithRetry(() => import("./pages/Leads"));
const LeadDetail = lazyWithRetry(() => import("./pages/LeadDetail"));
const Bookings = lazyWithRetry(() => import("./pages/Bookings"));
const Appointments = lazyWithRetry(() => import("./pages/Appointments"));
const Tasks = lazyWithRetry(() => import("./pages/Tasks"));
const Pipeline = lazyWithRetry(() => import("./pages/Pipeline"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Account = lazyWithRetry(() => import("./pages/Account"));
const Documents = lazyWithRetry(() => import("./pages/Documents"));
const Transactions = lazyWithRetry(() => import("./pages/Transactions"));
const TransactionDetail = lazyWithRetry(() => import("./pages/TransactionDetail"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const PublicBooking = lazyWithRetry(() => import("./pages/PublicBooking"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const AdminUserDetail = lazyWithRetry(() => import("./pages/AdminUserDetail"));
const AdminRevenue = lazyWithRetry(() => import("./pages/AdminRevenue"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const FollowUps = lazyWithRetry(() => import("./pages/FollowUps"));
const PhotoSessions = lazyWithRetry(() => import("./pages/PhotoSessions"));
const PhotoDeliveries = lazyWithRetry(() => import("./pages/PhotoDeliveries"));
const PhotographyInvoices = lazyWithRetry(() => import("./pages/PhotographyInvoices"));
const Accounting = lazyWithRetry(() => import("./pages/Accounting"));
const Inbox = lazyWithRetry(() => import("./pages/Inbox"));
const Communications = lazyWithRetry(() => import("./pages/Communications"));
const DoorKnocking = lazyWithRetry(() => import("./pages/DoorKnocking"));
const ToDo = lazyWithRetry(() => import("./pages/ToDo"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const AdminSupport = lazyWithRetry(() => import("./pages/AdminSupport"));
const Scorecard = lazyWithRetry(() => import("./pages/Scorecard"));
const SmartPlans = lazyWithRetry(() => import("./pages/SmartPlans"));
const Vendors = lazyWithRetry(() => import("./pages/Vendors"));
const BulkEmail = lazyWithRetry(() => import("./pages/BulkEmail"));
const BulkEmailComposer = lazyWithRetry(() => import("./pages/BulkEmailComposer"));
const SocialMedia = lazyWithRetry(() => import("./pages/SocialMedia"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data fresh for 5 minutes - prevents refetching on cellular
      staleTime: 5 * 60 * 1000,
      // Cache data for 30 minutes
      gcTime: 30 * 60 * 1000,
      // Don't refetch on window focus for better mobile performance
      refetchOnWindowFocus: false,
      // Retry failed requests once
      retry: 1,
    },
  },
});

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return session ? <>{children}</> : <Navigate to="/auth" />;
}

function RootRoute() {
  const { role, loading } = useUserRole();

  if (loading) {
    return <PageLoader />;
  }

  // Treat "/" as an alias of the user dashboard to avoid redirect loops.
  return role === "admin" ? <Navigate to="/admin" /> : <Dashboard />;
}

function DashboardRoute() {
  const { role, loading } = useUserRole();

  if (loading) {
    return <PageLoader />;
  }

  // If an admin hits /dashboard (or is redirected there after login), send them to /admin.
  return role === "admin" ? <Navigate to="/admin" /> : <Dashboard />;
}

function PhotographyRoute({ children }: { children: React.ReactNode }) {
  const { hasAccess, loading } = usePhotographyAccess();

  if (loading) {
    return <PageLoader />;
  }

  if (!hasAccess) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <VercelAnalytics />
        <SpeedInsights />
        <BrowserRouter>
          <ActivityTracker />
          <main className="min-h-screen">
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/auth" element={<AuthForm />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <RootRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads"
                element={
                  <ProtectedRoute>
                    <Leads />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads/:id"
                element={
                  <ProtectedRoute>
                    <LeadDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings"
                element={
                  <PhotographyRoute>
                    <Bookings />
                  </PhotographyRoute>
                }
              />
              <Route
                path="/appointments"
                element={
                  <ProtectedRoute>
                    <Appointments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <Tasks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pipeline"
                element={
                  <ProtectedRoute>
                    <Pipeline />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <Calendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <Account />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedRoute>
                    <Documents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <Transactions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions/:id"
                element={
                  <ProtectedRoute>
                    <TransactionDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <ProtectedRoute>
                    <AdminUserDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/faq"
                element={
                  <ProtectedRoute>
                    <FAQ />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/follow-ups"
                element={
                  <ProtectedRoute>
                    <FollowUps />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inbox"
                element={
                  <ProtectedRoute>
                    <Inbox />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/communications"
                element={
                  <ProtectedRoute>
                    <Communications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/door-knocking"
                element={
                  <ProtectedRoute>
                    <DoorKnocking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/todo"
                element={
                  <ProtectedRoute>
                    <ToDo />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <Support />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <ProtectedRoute>
                    <AdminSupport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/revenue"
                element={
                  <ProtectedRoute>
                    <AdminRevenue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scorecard"
                element={
                  <ProtectedRoute>
                    <Scorecard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/smart-plans"
                element={
                  <ProtectedRoute>
                    <SmartPlans />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vendors"
                element={
                  <ProtectedRoute>
                    <Vendors />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-email"
                element={
                  <ProtectedRoute>
                    <BulkEmail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-email-composer"
                element={
                  <ProtectedRoute>
                    <BulkEmailComposer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/social-media"
                element={
                  <ProtectedRoute>
                    <SocialMedia />
                  </ProtectedRoute>
                }
              />
                <Route
                  element={
                    <ProtectedRoute>
                      <PhotographyRoute>
                        <PhotoSessions />
                      </PhotographyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/photo-deliveries"
                  element={
                    <ProtectedRoute>
                      <PhotographyRoute>
                        <PhotoDeliveries />
                      </PhotographyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/photography-invoices"
                  element={
                    <ProtectedRoute>
                      <PhotographyRoute>
                        <PhotographyInvoices />
                      </PhotographyRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounting"
                  element={
                    <ProtectedRoute>
                      <PhotographyRoute>
                        <Accounting />
                      </PhotographyRoute>
                    </ProtectedRoute>
                  }
                />
              <Route path="/book/:slug" element={<PublicBooking />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

function ActivityTracker() {
  useActivityTracking();
  return null;
}

export default App;
